import httpClient from './httpClient';
import {
  AD_ACCOUNT_NAME_MAX_LENGTH,
  ADVERTISER_NAME_MAX_LENGTH,
  APP_DESCRIPTION_MAX_LENGTH,
  APP_NAME_MAX_LENGTH
} from "@/constants/maxLengths";
import qs from 'qs';
import authzApi from "@/api/authzApi";
import {setDifferenceObject} from "@/util/log"

const MASTER_API_PREFIX = "admaster/v1";
const ADMIN_API_PREFIX = "admaster/v1/admin";

const paramsSerializer = params => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true});

export default {
  findAllAdvertisers() {
    return httpClient.get(`${ADMIN_API_PREFIX}/advertisers`);
  },
  findAllAdvertisersWithout(skipData, gapAppIds) {
    const skip = skipData === 'campaign' ? skipData : 'app'

    const config = !!gapAppIds ? { params: { gapAppIds: gapAppIds } } : null

    return httpClient.get(`${ADMIN_API_PREFIX}/advertisers/without/${skip}`, config);
  },
  findAdvertiser(corporateRegistrationNumber) {
    return httpClient.get(`${MASTER_API_PREFIX}/advertisers/${corporateRegistrationNumber}`);
  },
  postAdvertiser(advertiser) {
    if (validateAdvertiserName(advertiser)) {
      return httpClient.post(`${ADMIN_API_PREFIX}/advertisers`, advertiser);
    }
  },
  getApps(advertiserCrn, page = 0, size = 10) {
    return httpClient.get(`${ADMIN_API_PREFIX}/advertisers/${advertiserCrn}/apps`,
                          {params: {page: page, size: size}});
  },
  getTalkChannels(corporateRegistrationNumber) {
    return httpClient.get(`${ADMIN_API_PREFIX}/advertisers/${corporateRegistrationNumber}/talk-channels`,
                          {params: {page: 0, size: 1000}}
    );
  },
  getAllTalkChannels() {
    return httpClient.get(`${ADMIN_API_PREFIX}/talk-channels?size=1000`);
  },
  getTalkChannel(gapTalkChannelId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/talk-channels/${gapTalkChannelId}`);
  },
  postTalkChannel(gapTalkChannel) {
    return httpClient.post(`${ADMIN_API_PREFIX}/talk-channels`, gapTalkChannel);
  },
  putTalkChannel(gapTalkChannelId, name) {
    return httpClient.put(`${ADMIN_API_PREFIX}/talk-channels/${gapTalkChannelId}`, {
      name: name
    });
  },
  validateTalkChannel(publicProfileId, corpRegNum) {
    return httpClient.post(`${ADMIN_API_PREFIX}/talk-channels/validate-talk-channel-profile-id`,
      {publicProfileId: publicProfileId, advertiserCorporateRegistrationNumber: corpRegNum});
  },
  getAppMasterApps(advertiserCrn) {
    return httpClient.get(`${ADMIN_API_PREFIX}/advertisers/${advertiserCrn}/app-master-apps`);
  },
  getAppKey(app) {
    const appId = (typeof app === "number") ? app : app.id;
    return httpClient.get(`${ADMIN_API_PREFIX}/apps/${appId}/key`);
  },

  getAdAccounts() {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts`)
  },
  getAdAccountsWithCampaigns() {
    return httpClient.get(`${MASTER_API_PREFIX}/ad-accounts?with-campaigns=true`)
  },
  getAdAccount(adAccountId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}`)
  },
  postAdAccount(adAccount) {
    if (validateAdAccountName(adAccount)) {
      return httpClient.post(`${ADMIN_API_PREFIX}/ad-accounts`, adAccount);
    }
  },
  putAdAccount(adAccount, name) {
    if (validateAdAccountName(name)) {
      return httpClient.put(`${ADMIN_API_PREFIX}/ad-accounts/${adAccount.id}`, {name: name});
    }
  },
  setAdAccountIsJoinRequestDenied(adAccount, isDenined) {
    return httpClient.put(`${ADMIN_API_PREFIX}/ad-accounts/${adAccount.id}`,
                          {isJoinRequestDenied: isDenined});
  },

  getAdAccountUsers(adAccountId, page = 0, size = 10) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/admin-users`,
                          {params: {page: page, size: size}});
  },
  searchAdAccountUsers(adAccountId, searchText) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/admin-users/search`, {
      params: {searchText: searchText}
    })
  },
  addUserToAdAccount(adAccount, userId) {
    if (validateAdminUserId(userId)) {
      if (userId.indexOf("::LDAP") === -1) {
        userId = userId + "::KAKAO";
      }
      return httpClient.post(`${ADMIN_API_PREFIX}/ad-accounts/${adAccount.id}/admin-users`, userId);
    }
  },
  removeUserFromAdAccount(adAccount, userId) {
    return httpClient.delete(`${ADMIN_API_PREFIX}/ad-accounts/${adAccount.id}/admin-users`,
                             {data: userId});
  },
  quitFromAdAccount(userId, adAccount) {
    userId = userId.replace("::LDAP", "");
    return httpClient.delete(
      `${ADMIN_API_PREFIX}/admin-users/${userId}/ad-accounts/${adAccount.id}`);
  },
  getAdAccountChangeLogs(adAccountId, page = 0, size = 10) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/change-logs`,
                          {params: {page: page, size: size}})
  },
  getAdAccountChangeLogsByAdminUserId(adminUserId, page = 0, size = 10) {
    return httpClient.get(`${ADMIN_API_PREFIX}/admin-users/${adminUserId}/ad-account-change-logs`,
                          {params: {page: page, size: size}})
  },

  getAdAccountsByUserId(userId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/admin-users/${userId}/ad-accounts`)
      .then(
        response => {
          response?.data?.data?.forEach(
            elm => {
              elm.id = elm.adAccountId;
              elm.name = elm.adAccountName;
            }
          );
          return response;
        }
      );
  },

  getAdAccountsByUserIdDetail(userId, page = 0, size = 10) {
    userId = userId.replace("::LDAP", "");
    return httpClient.get(`${ADMIN_API_PREFIX}/admin-users/${userId}/ad-accounts-detail`,
                          {params: {page: page, size: size}});
  },

  findAdAccountsByAdvertiser(corporateRegistrationNumber, page, size) {
    return httpClient.get(
      `${ADMIN_API_PREFIX}/advertisers/${corporateRegistrationNumber}/ad-accounts`,
      {params: {page: page, size: size}});
  },
  findAdAccountsWithMemberTypeByAdvertiser(corporateRegistrationNumber, adminUserId, page, size) {
    return httpClient.get(
      `${ADMIN_API_PREFIX}/advertisers/${corporateRegistrationNumber}/ad-accounts-with-member-type?adminUserId=${adminUserId}`,
      {params: {page: page, size: size}});
  },
  findAdAccountsWithMemberTypeByAgency(corporateRegistrationNumber, adminUserId, page, size) {
    return httpClient.get(
      `${ADMIN_API_PREFIX}/agencies/${corporateRegistrationNumber}/ad-accounts-with-member-type?adminUserId=${adminUserId}`,
      {params: {page: page, size: size}});
  },
  getCampaigns(adAccountId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/campaigns`);
  },
  getCampaign(campaignId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/campaigns/${campaignId}`);
  },
  postCampaign(createCampaignRequestBody) {
    return httpClient.post(`${ADMIN_API_PREFIX}/campaigns`, createCampaignRequestBody);
  },
  putCampaign(campaignId, editCampaignRequestBody) {
    return httpClient.put(`${ADMIN_API_PREFIX}/campaigns/${campaignId}`, editCampaignRequestBody);
  },
  getAgencies() {
    return httpClient.get(`${ADMIN_API_PREFIX}/agencies`);
  },
  findAgency(corporateRegistrationNumber) {
    return httpClient.get(`${MASTER_API_PREFIX}/agencies/${corporateRegistrationNumber}`);
  },
  getAdAccountsByAgencyCrn(agencyCrn) {
    return httpClient.get(`${ADMIN_API_PREFIX}/agencies/${agencyCrn}/ad-accounts`)
  },
  getAppsByAdAccount(adAccountId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/apps`);
  },
  searchApps(advertiserId, appName, atlasRegistered, page, size) {
    return httpClient.get(`admaster/v1/admin/apps`,
                          {
                            params: {
                              page: page,
                              size: size,
                              advertiserId: advertiserId,
                              appName: appName,
                              atlasRegistered: atlasRegistered
                            }
                          });
  },
  searchAppsWithoutCampaigns(advertiserId, appName, atlasRegistered, gapAppIds, appMasterIds, page, size) {
    return httpClient.get(`admaster/v1/admin/apps/without-campaigns`,
                          {
                            params: {
                              page: page, size: size,
                              advertiserId: advertiserId,
                              appName: appName,
                              atlasRegistered: atlasRegistered,
                              gapAppIds: gapAppIds,
                              appMasterIds: appMasterIds
                            }
                          });
  },
  getApp(appId) {
    return httpClient.get(`${ADMIN_API_PREFIX}/apps/${appId}`);
  },
  getAllApps() {
    return httpClient.get(`${MASTER_API_PREFIX}/apps`);
  },
  /***
   * 입접 게임을 광고 플랫폼에 등록
   * @param app
   * @return {*}
   */
  postGameCenterApp(app) {
    return httpClient.post(`${ADMIN_API_PREFIX}/apps`, app);
  },

  /***
   * 카프리앱에 앱을 새로 등록 후 광고 플랫폼에 등록
   * @param app
   * @return {*}
   */
  postCapriApp(app) {
    if (validateAppName(app) &&
        validateAppDescription(app)) {
      return httpClient.post(`${ADMIN_API_PREFIX}/capri-apps`, app);
    }
  },

  /***
   * 앱마스터에 새로운 앱을 만들어서 광고 플랫폼 앱에 매핑
   * @param app
   * @return {*}
   */
  postAppMasterApp(app) {
    return httpClient.post(`${ADMIN_API_PREFIX}/apps/${app.id}/app-master`, app);
  },

  /***
   * 이미 있는 앱마스터 앱에 광고 플랫폼 앱 매핑
   * @param app
   * @param appMasterId
   * @return {*}
   */
  registerAppMasterApp(app, appMasterId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/apps/${app.id}/app-master/${appMasterId}`);
  },

  putApp(app) {
    if (validateAppName(app) &&
        validateAppDescription(app)) {
      const putAppUrl = `${ADMIN_API_PREFIX}/apps/${app.id}`;
      return httpClient.put(putAppUrl, app);
    }
  },
  changeAppAdvertiser(app, advertiserId) {
    return httpClient.put(`${ADMIN_API_PREFIX}/apps/${app.id}/advertiser`, {
      currentAdvertiserId: app.advertiserId,
      advertiserId: advertiserId
    });
  },

  getAdAccountRequestListByAdminUserId(adminUserId, searchStatus, searchTypes, granterId, page = 0,
                                       size = 10) {
    adminUserId = adminUserId.replace("::LDAP", "");
    return httpClient.get(`${ADMIN_API_PREFIX}/admin-users/${adminUserId}/ad-account-requests`,
                          {
                            params: {
                              searchStatus: searchStatus,
                              searchType: searchTypes,
                              granterId: granterId,
                              page: page,
                              size: size
                            },
                            paramsSerializer
                          });
  },
  getAdAccountRequestListByAdAccountId(adAccountId, searchStatus, searchTypes, granterId, page = 0,
                                       size = 10) {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-accounts/${adAccountId}/requests`,
                          {
                            params: {
                              searchStatus: searchStatus,
                              searchType: searchTypes,
                              granterId: granterId,
                              page: page,
                              size: size
                            },
                            paramsSerializer: paramsSerializer
                          });
  },
  makeAdAccountJoinRequest(adAccountId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/requests/join`,
                           {adAccountId: adAccountId});
  },
  makeAdAccountHandoverRequest(adAccountId, targetUserId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/requests/handover`,
                           {
                             adAccountId: adAccountId,
                             granterId: targetUserId
                           });
  },
  cancelAdAccountRequest(adAccountRequestId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/requests/${adAccountRequestId}/cancel`);
  },
  grantAdAccountRequest(adAccountRequestId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/requests/${adAccountRequestId}/grant`);
  },
  rejectAdAccountRequest(adAccountRequestId) {
    return httpClient.post(`${ADMIN_API_PREFIX}/requests/${adAccountRequestId}/reject`);
  },
  postAdFile(adFile) {
    return httpClient.post(`${ADMIN_API_PREFIX}/ad-files`, adFile);
  },
  getAdFiles() {
    return httpClient.get(`${ADMIN_API_PREFIX}/ad-files`);
  },
  putAdFiles(id, adFile) {
    return httpClient.put(`${ADMIN_API_PREFIX}/ad-files/${id}`, adFile);
  },
  uploadAdFile(name, file) {
    let formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);

    return httpClient.post(`${ADMIN_API_PREFIX}/ad-files/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  deleteAdFile(id) {
    return httpClient.delete(`${ADMIN_API_PREFIX}/ad-files/${id}`);
  },
}

const validateAppName = function (app) {
  let name = app.name || app;
  name = name.trim();
  if (typeof name !== "string" ||
      !name || name.length === 0) {
    alert("앱 이름을 입력해주세요.");
    return false;
  } else if (name.length > APP_NAME_MAX_LENGTH) {
    alert(`앱 이름은 ${APP_NAME_MAX_LENGTH}자 이하로 입력해주세요.`);
    return false;
  }
  return true;
};

const validateAppDescription = function (app) {
  let name = app.description || app;
  if (name.length > APP_DESCRIPTION_MAX_LENGTH) {
    alert(`앱 설명은 ${APP_DESCRIPTION_MAX_LENGTH}자 이하로 입력해주세요.`);
    return false;
  }
  return true;
};

const validateAdAccountName = function (adAccount) {
  let name = adAccount.name || adAccount;
  name = name.trim();
  if (typeof name !== "string" ||
      !name || name.length === 0) {
    alert("광고 계정명을 입력해주세요.");
    return false;
  } else if (name.length > AD_ACCOUNT_NAME_MAX_LENGTH) {
    alert(`광고 계정명은 ${AD_ACCOUNT_NAME_MAX_LENGTH}자 이하로 입력해주세요.`);
    return false;
  }
  return true;
};

const validateAdminUserId = function (adminUserId) {
  let name = adminUserId.name || adminUserId;
  name = name.trim();
  if (typeof name !== "string" ||
      !name || name.length === 0) {
    alert("추가할 계정을 입력해주세요.");
    return false;
  }
  return true;
};

const validateAdvertiserName = function (advertiser) {
  let name = advertiser.name || advertiser;
  name = name.trim();
  if (typeof name !== "string" ||
      !name || name.length === 0) {
    alert("사업자명을 입력해주세요.");
    return false;
  } else if (name.length > ADVERTISER_NAME_MAX_LENGTH) {
    alert(`사업자명은 ${ADVERTISER_NAME_MAX_LENGTH}자 이하로 입력해주세요.`);
    return false;
  } else if (!validateCorporationName(name)) {
    alert("올바른 사업자명을 입력해주세요.");
    return false;
  }
  return true;
};

export const validateCorporationName = function (name) {
  return /^[㈜ a-zA-Z0-9.&',∙가-힣\-\\(\\)]+$/g.test(name);
};
